/**
 * Chat Storage Utility
 * Manages localStorage persistence for chat history
 * Following production-grade storage patterns
 */

import type { ChatConversation } from '../types/chat.types';

const STORAGE_KEY = 'amalq_chat_history';
const MAX_CONVERSATIONS = 50;
const MAX_MESSAGE_LENGTH = 5000;

/**
 * Get all stored conversations
 * @param userId - Optional user ID to filter conversations (only show conversations for this user or guest conversations)
 */
export function getStoredConversations(userId?: string): ChatConversation[] {
  try {
    // Check if localStorage is available
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return [];
    }
    
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const conversations = JSON.parse(stored) as ChatConversation[];
    
    // Validate and clean up corrupted data
    const validConversations = conversations
      .filter((conv): conv is ChatConversation => 
        conv && 
        typeof conv === 'object' && 
        Array.isArray(conv.messages) &&
        typeof conv.id === 'string' &&
        typeof conv.startedAt === 'number'
      );
    
    // Filter by userId if provided (security: only show conversations for current user)
    // Guest conversations (isGuest: true) are shown to all users
    // Authenticated conversations are only shown to their owner
    if (userId) {
      return validConversations
        .filter(conv => {
          // Show guest conversations to everyone
          if (conv.isGuest) return true;
          // Show conversations that belong to this user
          return conv.userId === userId;
        })
        .slice(0, MAX_CONVERSATIONS);
    }
    
    // If no userId provided, only return guest conversations (for unauthenticated users)
    return validConversations
      .filter(conv => conv.isGuest === true)
      .slice(0, MAX_CONVERSATIONS);
  } catch (error) {
    console.error('Failed to load chat history:', error);
    // Clear corrupted data
    localStorage.removeItem(STORAGE_KEY);
    return [];
  }
}

/**
 * Save conversations to localStorage
 */
export function saveConversations(conversations: ChatConversation[]): void {
  try {
    // Check if localStorage is available
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    // Limit to max conversations (keep most recent)
    const limited = conversations
      .sort((a, b) => b.lastActivityAt - a.lastActivityAt)
      .slice(0, MAX_CONVERSATIONS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Failed to save chat history:', error);
    // Handle quota exceeded error
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      // Remove oldest conversations
      const limited = conversations
        .sort((a, b) => b.lastActivityAt - a.lastActivityAt)
        .slice(0, Math.floor(MAX_CONVERSATIONS / 2));
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
      } catch {
        // If still fails, clear all
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }
}

/**
 * Save a single conversation
 */
export function saveConversation(conversation: ChatConversation): void {
  const conversations = getStoredConversations();
  const existingIndex = conversations.findIndex(c => c.id === conversation.id);
  
  if (existingIndex >= 0) {
    conversations[existingIndex] = conversation;
  } else {
    conversations.push(conversation);
  }
  
  saveConversations(conversations);
}

/**
 * Get a specific conversation by ID
 */
export function getConversation(conversationId: string): ChatConversation | null {
  const conversations = getStoredConversations();
  return conversations.find(c => c.id === conversationId) || null;
}

/**
 * Delete a conversation
 */
export function deleteConversation(conversationId: string): void {
  const conversations = getStoredConversations();
  const filtered = conversations.filter(c => c.id !== conversationId);
  saveConversations(filtered);
}

/**
 * Clear all chat history
 */
export function clearChatHistory(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Validate message content
 */
export function validateMessage(content: string): { valid: boolean; error?: string } {
  if (!content || !content.trim()) {
    return { valid: false, error: 'Message cannot be empty' };
  }
  
  if (content.length > MAX_MESSAGE_LENGTH) {
    return { valid: false, error: `Message must be less than ${MAX_MESSAGE_LENGTH} characters` };
  }
  
  return { valid: true };
}

/**
 * Generate unique conversation ID (UUID v4 format)
 * Backend requires valid GUID format for conversationId
 */
export function generateConversationId(): string {
  // Generate UUID v4 format (RFC 4122)
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
  return template.replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === 'x' ? random : (random & 0x3 | 0x8);
    return value.toString(16);
  });
}

/**
 * Generate unique message ID
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

